import React from "react";
import {
  ApiResponse,
  ApiResponseMention,
  ApiResponsesList,
} from "../../api/types/api-response";
import { useSearchParams } from "react-router-dom";
import { useRequiredParams } from "../../utils/use-required-params";
import { useGetCategoriesApi } from "../../api/methods/use-get-categories-api";
import { useGetThemesApi } from "../../api/methods/use-get-themes-api";
import { useGetOrganizationDetailsApi } from "../../api/methods/use-get-organization-details-api";
import { useCountReviewedResponses } from "../../utils/use-count-reviewed-responses";
import { useGetResponseStatisticsApi } from "../../api/methods/use-get-response-statistics-api";
import { CategoryWithThemes } from "./types";
import { useCategoriesWithThemes } from "../../utils/use-categories-with-themes";
import { getFormattedDate } from "../../utils/get-formatted-date";
import { formatCount } from "../../utils/format";
import { ResponseCommentTextarea } from "./response-comment-textarea";
import { ThemesInputInline } from "./themes-input-inline";
import { CategoriesWithThemesListModern } from "./categories-with-themes-list-modern";
import { useGetSummary } from "../../utils/use-get-summary";
import { useKeyboardShortcuts, isMac } from "../../utils/use-keyboard-shortcuts";
import { useResponseTimer } from "../../utils/use-response-timer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Kbd } from "@/components/ui/kbd";

export const TaggingModern: React.FC<{
  currentResponse: ApiResponse;
  responsesResponse: ApiResponsesList;
  bufferCount: number;
  onSkipResponse: () => void;
  onPreviousResponse: () => void;
  onUpdateResponse: (mentions: ApiResponseMention[]) => void;
  isDisabled: boolean;
}> = (props) => {
  const [searchParams] = useSearchParams();
  const { organizationId, responseId } = useRequiredParams<{
    organizationId: string;
    responseId: string;
  }>();
  const themeStructureId = searchParams.get("themeStructureId") as string;
  const [mentions, setMentions] = React.useState<ApiResponseMention[]>(
    props.currentResponse.attributes.mentions
  );
  React.useEffect(() => {
    setMentions(props.currentResponse.attributes.mentions);
  }, [props.currentResponse.id]);

  const { data: categories } = useGetCategoriesApi({
    organizationId: organizationId,
    themeStructureId: themeStructureId,
  });
  const { data: themes } = useGetThemesApi({
    organizationId: organizationId,
    themeStructureId: themeStructureId,
  });
  const { data: organization } = useGetOrganizationDetailsApi({
    organizationId: organizationId,
  });

  const { count } = useCountReviewedResponses();
  const { data: responsesStatistics } = useGetResponseStatisticsApi("", {
    refetchOnMount: false,
    enabled: false,
  });

  const {
    summary,
    getSummary: handleGetSummary,
    isLoading: isSummaryLoading,
  } = useGetSummary({
    organizationId,
    responseId,
  });

  const { formattedTime, timerColor } = useResponseTimer(responseId);

  const saveHandler = () => {
    if (mentions.length > 0) {
      props.onUpdateResponse(mentions);
    }
  };


  const skipHandler = () => {
    props.onSkipResponse();
  };

  const previousHandler = () => {
    props.onPreviousResponse();
  };

  useKeyboardShortcuts([
    {
      key: "Enter",
      modKey: true,
      handler: saveHandler,
      description: "Save response",
    },
    {
      key: "Escape",
      handler: () => {
        setMentions([]);
      },
      description: "Reset changes",
    },
    {
      key: "ArrowRight",
      modKey: true,
      handler: skipHandler,
      description: "Skip to next response",
    },
    {
      key: "ArrowLeft",
      modKey: true,
      handler: previousHandler,
      description: "Go to previous response",
    },
  ]);

  const currentOrganizationResponsesTotal = responsesStatistics?.find(
    (organization) =>
      organization.attributes.organization_id === Number(organizationId)
  )?.attributes.value;

  const categoriesWithThemes: CategoryWithThemes[] = useCategoriesWithThemes(
    categories,
    themes
  );

  const handleThemeAddOrUpdate = (mention: ApiResponseMention) => {
    setMentions((prevMentions) => {
      return prevMentions.find((m) => m.theme_id === mention.theme_id)
        ? prevMentions.map((m) =>
            m.theme_id === mention.theme_id ? mention : m
          )
        : [...prevMentions, mention];
    });
  };

  const handleThemeRemove = (mention: ApiResponseMention) => {
    setMentions((prevMentions) => {
      return prevMentions.filter((m) => m.theme_id !== mention.theme_id);
    });
  };

  return (
    <div
      className={`container mx-auto px-4 pb-4 space-y-3 ${
        props.isDisabled ? "opacity-50 pointer-events-none" : ""
      }`}
    >
      <div className="sticky top-14 z-40 pb-1 pt-1 bg-background space-y-3">
        <Card>
          <CardContent className="p-2">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <div>
                ID: {props.currentResponse.id} • {getFormattedDate(props.currentResponse.attributes.created_at)} • {organization?.attributes.name}
              </div>
              <div>
                <span className={timerColor}>
                  {formattedTime}
                </span>
                {' '}| Buffer: {formatCount(props.bufferCount)} | Today: {formatCount(count)} | Left: {formatCount(props.responsesResponse.total)}
                {currentOrganizationResponsesTotal && ` | Total: ${formatCount(currentOrganizationResponsesTotal)}`}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3">
            <ResponseCommentTextarea
              response={props.currentResponse}
              onGetSummary={handleGetSummary}
              summary={summary}
              isSummaryLoading={isSummaryLoading}
              onSkip={props.onSkipResponse}
            />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-3">
          <div className="flex gap-2 items-start">
            <div className="flex-1">
              <ThemesInputInline
                mentions={mentions}
                themes={themes || []}
                categories={categories || []}
                onMentionRemove={handleThemeRemove}
                onMentionAdd={handleThemeAddOrUpdate}
              />
            </div>

            <TooltipProvider>
              <div className="flex gap-1 pt-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-block">
                      <Button
                        onClick={() => props.onUpdateResponse(mentions)}
                        disabled={mentions.length === 0}
                        size="icon"
                        className="h-9 w-9"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                          <polyline points="17 21 17 13 7 13 7 21"/>
                          <polyline points="7 3 7 8 15 8"/>
                        </svg>
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="flex items-center gap-1">
                      Save <Kbd>{isMac() ? "⌘" : "Ctrl"}</Kbd><Kbd>Enter</Kbd>
                    </div>
                  </TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-block">
                      <Button
                        variant="outline"
                        onClick={() => setMentions([])}
                        disabled={mentions.length === 0}
                        size="icon"
                        className="h-9 w-9"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                          <path d="M21 3v5h-5"/>
                          <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                          <path d="M8 16H3v5"/>
                        </svg>
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div className="flex items-center gap-1">
                      Reset <Kbd>Esc</Kbd>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-3">
          <CategoriesWithThemesListModern
            categoriesWithThemes={categoriesWithThemes}
            onThemeClick={handleThemeAddOrUpdate}
          />
        </CardContent>
      </Card>
    </div>
  );
};
